import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BillingSection } from "@/components/billing-section.js";
import { FullPageModal } from "@/components/layout/full-page-modal";
import { AccessTokensTab } from "@/components/settings/access-tokens-tab.js";
import { ExportsTab } from "@/components/settings/export-config-card.js";
import { GeneralTab } from "@/components/settings/general-tab";
import { MembersTab } from "@/components/settings/members-tab.js";
import { ScrollFade } from "@/components/ui/scroll-fade";
import { cn } from "@/lib/utils";
import { IntegrationContent } from "./integration.js";

export { GeneralTab };

const settingsSections = [
  {
    id: "general",
    label: "General",
    description: "Workspace information and configuration.",
  },
  {
    id: "members",
    label: "Members",
    description: "Manage team members, roles, and workspace invitations.",
  },
  {
    id: "billing",
    label: "Billing",
    description: "Workspace plan, subscription, and resource limits.",
  },
  {
    id: "access-tokens",
    label: "Access tokens",
    description: "Tokens for authenticating MCP servers and API clients.",
  },
  {
    id: "exports",
    label: "Auto-fix agents",
    description:
      "Active 2-hour agent work leases and lock monitoring across workspace projects.",
  },
  {
    id: "integrations",
    label: "Integrations",
    description: "Connect AI agents and external services.",
  },
];

function normalizeSettingsTab(rawTab: string | null): string | null {
  if (!rawTab) return null;
  if (rawTab === "usage") return "billing";
  if (rawTab === "agents" || rawTab === "export") return "exports";
  if (rawTab === "tokens" || rawTab === "api-keys") return "access-tokens";
  return settingsSections.some((s) => s.id === rawTab) ? rawTab : "general";
}

export function SettingsHubPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const rawTab = searchParams.get("tab");
  const normalizedTab = normalizeSettingsTab(rawTab);
  const [activeSection, setActiveSection] = useState(
    normalizedTab ?? "general",
  );
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (rawTab && normalizedTab && rawTab !== normalizedTab) {
      setSearchParams({ tab: normalizedTab }, { replace: true });
    }
    if (normalizedTab) {
      setActiveSection(normalizedTab);
    }
  }, [rawTab, normalizedTab, setSearchParams]);

  const selectTab = (tabId: string) => {
    setActiveSection(tabId);
    setSearchParams({ tab: tabId }, { replace: true });
    const scroller = contentRef.current?.closest(
      ".overflow-y-auto",
    ) as HTMLElement | null;
    if (scroller) {
      scroller.scrollTo({ top: 0, behavior: "instant" });
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  };

  const handleClose = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate("/");
  };

  const currentSection =
    settingsSections.find((s) => s.id === activeSection) ?? settingsSections[0];

  return (
    <FullPageModal
      title="Settings"
      subtitle="Manage your workspace configuration"
      onClose={handleClose}
      stickyHeader={true}
    >
      <div
        ref={contentRef}
        className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8"
      >
        <div className="sticky top-[65px] sm:top-20 z-20 -mx-4 sm:-mx-6 mb-8 border-b bg-background/95 px-4 sm:px-6 py-2 backdrop-blur-xs lg:hidden">
          <ScrollFade className="flex gap-2 py-1">
            {settingsSections.map((section) => (
              <button
                key={section.id}
                type="button"
                aria-current={activeSection === section.id ? "true" : undefined}
                onClick={() => selectTab(section.id)}
                className={cn(
                  "rounded-sm px-3 py-1 text-xs font-medium whitespace-nowrap transition-colors",
                  activeSection === section.id
                    ? "bg-foreground text-background font-semibold shadow-xs"
                    : "bg-muted text-muted-foreground hover:text-foreground",
                )}
              >
                {section.label}
              </button>
            ))}
          </ScrollFade>
        </div>

        <div className="lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-12">
          <nav
            className="sticky top-28 hidden space-y-1 self-start lg:block"
            aria-label="Settings navigation"
          >
            <div className="mb-2 px-3 text-xs font-medium text-muted-foreground">
              Workspace
            </div>
            {settingsSections.map((section) => (
              <button
                key={section.id}
                type="button"
                aria-current={activeSection === section.id ? "true" : undefined}
                onClick={() => selectTab(section.id)}
                className={cn(
                  "flex w-full items-center rounded-md px-3 py-2 text-xs font-medium transition-colors text-left",
                  activeSection === section.id
                    ? "bg-foreground text-background font-semibold shadow-xs"
                    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                )}
              >
                {section.label}
              </button>
            ))}
          </nav>

          <div className="min-w-0 pb-24">
            <section
              key={currentSection.id}
              id={`section-${currentSection.id}`}
              className="space-y-4"
            >
              <div className="border-b border-border/60 pb-3">
                <h2 className="text-base font-semibold tracking-tight">
                  {currentSection.label}
                </h2>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {currentSection.description}
                </p>
              </div>
              {currentSection.id === "general" && <GeneralTab />}
              {currentSection.id === "members" && <MembersTab />}
              {currentSection.id === "billing" && (
                <BillingSection hideTitle={true} />
              )}
              {currentSection.id === "access-tokens" && <AccessTokensTab />}
              {currentSection.id === "exports" && <ExportsTab />}
              {currentSection.id === "integrations" && <IntegrationContent />}
            </section>
          </div>
        </div>
      </div>
    </FullPageModal>
  );
}
